import { Fragment, useContext, useEffect, useState } from "react";
import { Button } from "components/Button";
import { Typography } from "components/Typography";
import { handleValuationService, updateCardItemService } from "services/cart";
import styled from "styled-components";
import { fetchCartByIdService, downloadCardService } from "services/cart";
import { getCartId } from "utils/getCartId";
import { Loader } from "components/Loader";
import { ValuationModal } from "./ValuationModal";
import { CalculatorContext } from "App";

export interface IProps {
  items: any[];
  handleRemove: (id: string) => void;
  handleClear: (id: string) => void;
}
export interface ISumCart {
  sumLength: number;
  sumWeight: number;
}

const List = styled.div`
  display: none;
  width: 100%;
  margin-top: 0px;
  position: relative;
  @media (min-width: 800px) {
    margin-top: 64px;
    display: block;
  }
`;
const ElementList = styled.div`
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
  gap: 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.3);
  padding-top: 1rem;
  @media (min-width: 800px) {
    grid-template-columns: repeat(4, 1fr);
    padding: 16px;
    column-gap: 1.5rem;
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    align-items: center;
  }
`;
const ActionCartBtn = styled(Typography)`
  color: #c42f19;
  cursor: pointer;
`;
const Pipe = styled.div`
  margin: 0 5px;
`;
const Flex = styled.div`
  display: flex;
  align-items: baseline;
  align-items: center;
`;
const SumTypography = styled(Typography)`
  color: rgb(0, 48, 87);
  font-weight: 700;
  margin: 0 20px;
`;

const IconImage = styled.img`
  width: 20px; /* adjust width as needed */
  height: 20px; /* adjust height as needed */
  margin-right: 5px; /* adjust spacing as needed */
`;

export const Cart = ({ items, handleRemove, handleClear }: IProps) => {
  const [itemWeights, setItemWeights] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const context = useContext(CalculatorContext);

  const { translations } = context!;

  const [query, setQuery] = useState({ quantity: 0, id: "" });
  const [sumCart, setSumCart] = useState<ISumCart>({
    sumLength: 0,
    sumWeight: 0,
  });

  const fetchCardById = async () => {
    try {
      setIsLoading(true); // Set loading state to true
      const response = await fetchCartByIdService(getCartId());
      if (response.status === 200) {
        const sum = {
          sumLength: response.data.sumLength,
          sumWeight: response.data.sumWeight,
        };
        setSumCart(sum);
        const updatedItemWeights = {};
        response.data.items.forEach((item) => {
          updatedItemWeights[item.id] = item.weight;
        });
        setItemWeights(updatedItemWeights);
      }
    } finally {
      setIsLoading(false); // Set loading state to false after asynchronous code
    }
  };

  const handleChange = async () => {
    if (!query.id) return;

    setIsLoading(true); // Set loading state to true

    try {
      await updateCardItemService(query.id, {
        quantity: query.quantity,
      });
      fetchCardById();
    } finally {
      setIsLoading(false); // Set loading state to false after asynchronous code
    }
  };

  const handleDownload = async () => {
    const response = await downloadCardService(getCartId());
    if (response.status === 200) {
      const href = response.config.url;
      const link = document.createElement("a");
      link.href = href;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsSubmitted(false);
  };

  const handleValuation = async (formData: any) => {
    const response = await handleValuationService(getCartId(), formData);
    if (response.status === 200) {
      console.log(response.data);
    }
  };

  useEffect(() => {
    fetchCardById();
  }, [items]);

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      handleChange();
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [query.id, query.quantity]);

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    setQuery({ quantity: newQuantity, id: itemId });
    fetchCardById();
  };

  useEffect(() => {
    fetchCardById();
  }, [items]);

  return (
    <>
      {items.length > 0 && (
        <>
          <List>
            {isLoading && <Loader />}
            <ElementList>
              <Typography variant="small" fontWeight={700}>
                {translations["the-format-of-item"]}
              </Typography>
              <Typography variant="small" fontWeight={700}>
                {translations["count-of-items"]}
              </Typography>
              <Typography variant="small" fontWeight={700}>
                {translations["weight-of-item"]}
              </Typography>
              <Typography variant="small" fontWeight={700}>
                {translations["actions-of-item"]}
              </Typography>
            </ElementList>
            <ElementList className="desktop-table">
              {items.map((item) => (
                <Fragment key={item.id}>
                  <Typography>
                    <Flex>
                      <IconImage src={item.profile.iconUrl} alt="Icon" />
                      {item.filledFieldList.map((field, index) => (
                        <Fragment key={field.shortName}>
                          <Typography variant="small">{field.value}</Typography>
                          {index < item.filledFields.length - 1 && (
                            <Pipe>x</Pipe>
                          )}
                        </Fragment>
                      ))}
                    </Flex>
                  </Typography>
                  <div>
                    <input
                      type="number"
                      defaultValue={item.quantity}
                      className="cart-input"
                      min={1}
                      onKeyDown={(event) => {
                        if (
                          !(
                            (event.keyCode >= 48 && event.keyCode <= 57) || // Numeric keys
                            (event.keyCode >= 96 && event.keyCode <= 105) || // Numeric keypad
                            event.keyCode === 8 || // Backspace
                            event.keyCode === 46 || // Delete
                            event.keyCode === 37 || // Left arrow
                            event.keyCode === 39 ||
                            event.keyCode === 9
                          )
                        ) {
                          event.preventDefault();
                        }
                      }}
                      onChange={(e) =>
                        handleQuantityChange(
                          item.id,
                          Number(e.currentTarget.value)
                        )
                      }
                    />
                  </div>
                  <Typography>{itemWeights[item.id]} kg</Typography>
                  <div>
                    <button
                      className="remove-item"
                      onClick={() => handleRemove(item.id)}
                    >
                      {translations["remove-item"]}
                    </button>
                  </div>
                </Fragment>
              ))}
            </ElementList>

            <Flex style={{ gap: "1rem", marginTop: "3rem" }}>
              <button className="btn-send" onClick={openModal}>
                {translations["send-for-quote"]}
              </button>
              <Button fontWeight={700} onClick={handleDownload}>
                {translations["get-the-list"]}
              </Button>
              <SumTypography>
                {translations["sum-of-items"]}: {sumCart.sumWeight.toFixed(1)}
                kg, {sumCart.sumLength.toFixed(1)}m
              </SumTypography>
              <ActionCartBtn onClick={handleClear}>
                {translations["remove-everything"]}
              </ActionCartBtn>
            </Flex>
          </List>
          <ValuationModal
            isOpen={isModalOpen}
            handleValuation={handleValuation}
            handleClose={closeModal}
          />
        </>
      )}
    </>
  );
};
